import type { SupabaseClient } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { createDebounced } from "../dashboard/liveRefresh";
import { IS_DEMO } from "../env";

/** Trailing debounce and max wait: one refetch per burst, still within spec 04's 2 s. */
export const INVALIDATE_DEBOUNCE_MS = 750;
export const INVALIDATE_MAX_WAIT_MS = 2000;

let channelSeq = 0;

/** No Realtime in the FakeRest demo build, nor without a configured Supabase URL (unit tests). */
export const defaultRealtimeClient: (() => SupabaseClient) | null =
  IS_DEMO || !import.meta.env.VITE_SUPABASE_URL ? null : getSupabaseClient;

/**
 * Invalidates react-admin queries of `resource` (query key[0]) on any change of `table`
 * (Realtime postgres_changes; the table must be in the supabase_realtime publication).
 * No client (the FakeRest demo build) → no-op. Each mount gets its own channel name: supabase-js
 * hands back an existing channel for a known topic, and adding callbacks after subscribe() fails.
 */
export function useRealtimeInvalidate(
  table: string,
  resource: string,
  filter?: string,
  getClient: (() => SupabaseClient) | null = defaultRealtimeClient,
) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!getClient) return;
    const client = getClient();
    const invalidate = createDebounced(
      () => void qc.invalidateQueries({ queryKey: [resource] }),
      INVALIDATE_DEBOUNCE_MS,
      INVALIDATE_MAX_WAIT_MS,
    );
    const name = `gavion-${table}-${filter ?? "all"}-${++channelSeq}`;
    const channel = client
      .channel(name)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, ...(filter ? { filter } : {}) },
        invalidate,
      )
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT")
          console.warn(`[gavion-realtime] ${table} ${status}`, err ?? "");
      });
    return () => {
      invalidate.cancel();
      void client.removeChannel(channel);
    };
  }, [qc, table, resource, filter, getClient]);
}
